import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {filter, Observable, shareReplay, take, tap} from "rxjs";
import {BokkzIbDataService, IncomeTaxEntity, Onderneming} from "@snelstart-ventures/fiscaal/domain-logic";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {SharedUiButtonModule} from "@snelstart/ui-button";
import {SharedUiDialogModule} from "@snelstart/ui-dialog";
import {SharedUiFormControlsModule} from "@snelstart/ui-form-controls";
import {SharedUiDetailsModule} from "@snelstart/ui-details";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-spec-machines-installaties',
  standalone: true,
  imports: [CommonModule, SharedUiButtonModule, SharedUiDialogModule, SharedUiFormControlsModule, SharedUiDetailsModule],
  templateUrl: './spec-machines-installaties.component.html',
  styleUrl: './spec-machines-installaties.component.scss',
})
export class SpecMachinesInstallatiesComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  public dataService = inject(BokkzIbDataService);
  private destroyRef = inject(DestroyRef);
  public indexOnderneming = 0;
  public dialogVisible = false;
  public isPartner = false;

  public formGroup = new FormGroup({
    waardeOpening: new FormControl<number | null>(null),
    waardeSlot: new FormControl<number | null>(null),
    restWaarde: new FormControl<number | null>(null, Validators.required),
    aanschafWaarde: new FormControl<number | null>(null, Validators.required),
    omschrijving: new FormControl<string | null>('machinesEnInstallaties')
  })

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  readonly indexOnderneming$: Observable<number> =
    this.dataService.indexOnderneming$.pipe(
      tap(data => {
        this.indexOnderneming = data;
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.indexOnderneming$.subscribe();
    this.dataService.partnerAangifte$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(partner => {
      this.isPartner = partner;
      this.incomeTaxEntity$.pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
    });
  }

  showDialog() {
    this.handleSwitch(this.dataService.incomeTaxEntity.value, this.isPartner);
    this.dialogVisible = true;
  }

  private handleSwitch(entity: IncomeTaxEntity, isPartner: boolean): void {
    const details = isPartner ? entity.aangifte?.partnerAangifte : entity.aangifte;
    if (details) {
      this.resetAndPatchForms(details);
    }
  }

  private resetAndPatchForms(details: any): void {
    this.resetFormGroup();
    const ondernemingen: Onderneming[] = details.box1?.winstUitOnderneming?.ondernemingen || [];
    this.patchFormGroups(ondernemingen);
  }

  private resetFormGroup() {
    this.formGroup.setValue({
      waardeOpening: null as unknown as number,
      waardeSlot: null as unknown as number,
      aanschafWaarde: null as unknown as number,
      restWaarde: null as unknown as number,
      omschrijving: 'machinesEnInstallaties'
    }, {emitEvent: false})
  }

  private patchFormGroups(ondernemingen: Onderneming[]) {
    this.resetFormGroup();
    const onderneming = ondernemingen[this.indexOnderneming];
    if (!onderneming) return;
    if (onderneming.materieleVasteActiva?.at(1)) {
      // machines en installaties staat altijd op positie 1 in de json
      this.formGroup.patchValue(onderneming.materieleVasteActiva[1], {emitEvent: false});
    }
  }

  ngOnDestroy(): void {
    // rely on saveFrom in component
  }

  saveForm(): void {
    const idx = this.indexOnderneming;
    const formValue = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity((currentEntity) => {
      if (!currentEntity.aangifte) {
        return currentEntity;
      }
      const update = (aangifte: any) => {
        const existingOndernemingen = aangifte.box1?.winstUitOnderneming?.ondernemingen || [];
        const onderneming = existingOndernemingen[idx] || {};

        // Clone or create the array
        const materieleVasteActiva = Array.isArray(onderneming.materieleVasteActiva)
          ? [...onderneming.materieleVasteActiva]
          : [];
        // Ensure the array has at least 2 items
        while (materieleVasteActiva.length < 2) {
          materieleVasteActiva.push({});
        }
        // Update the second item
        materieleVasteActiva[1] = formValue;

        const updatedOnderneming: Partial<Onderneming> = {
          ...onderneming,
          materieleVasteActiva,
        };
        // Clean all empty arrays, arrays of empty objects, and empty objects from the outgoing data
        const cleanedOnderneming = this.dataService.deepClean(updatedOnderneming);
        const updatedOndernemingen = existingOndernemingen.map((o: Onderneming, i: number) => i === idx ? cleanedOnderneming : o);
        return {
          ...aangifte,
          box1: {
            ...aangifte.box1,
            winstUitOnderneming: {
              ...aangifte.box1?.winstUitOnderneming,
              ondernemingen: updatedOndernemingen,
            },
          },
        };
      };
      const updatedAangifte = this.isPartner
        ? {
            ...currentEntity.aangifte,
            partnerAangifte: update(currentEntity.aangifte?.partnerAangifte!),
          }
        : update(currentEntity.aangifte);
      return this.dataService.deepClean({
        ...currentEntity,
        aangifte: updatedAangifte,
      }) as IncomeTaxEntity;
    });
  }
}
